import React, { useEffect, useState, useRef } from "react";
import { IconButton, Box, Slider, Stack } from "@mui/material";
import { PlayArrow, VolumeUp, VolumeOff } from "@mui/icons-material";
import { Col, Row } from "react-bootstrap";
import PauseIcon from "@mui/icons-material/Pause";

export default function AudioPlayer(props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  // const [tabHasFocus, setTabHasFocus] = useState(true); // useState(false);
  const tabHasFocus = true;
  const [audioTime, setAudioTime] = useState(0);

  const audioRef = useRef(null);

  const endedHandlerRef = useRef(() => setIsPlaying(false));
  const timeupdateHandlerRef = useRef((event) => {
    setAudioTime(event.target.currentTime);
  });

  // useEffect(() => {
  //   const handleFocus = () => setTabHasFocus(true);
  //   const handleBlur = () => setTabHasFocus(false);

  //   window.addEventListener('focus', handleFocus);
  //   window.addEventListener('blur', handleBlur);
  //   window.addEventListener('click', handleFocus);

  //   return () => {
  //     window.removeEventListener('focus', handleFocus);
  //     window.removeEventListener('blur', handleBlur);
  //     window.removeEventListener('click', handleFocus);
  //   };
  // }, []);

  useEffect(() => {
    // Capture handler refs at effect execution time
    const endedHandler = endedHandlerRef.current;
    const timeupdateHandler = timeupdateHandlerRef.current;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener("ended", endedHandler);
      audioRef.current.removeEventListener("timeupdate", timeupdateHandler);
      audioRef.current.src = "";
    }

    const audio = new Audio(props.audio);
    audioRef.current = audio;
    setAudioTime(0);

    if (tabHasFocus && props.audioObject?.autoPlay && !props.promptVisible) {
      audio.play().catch((e) => console.error("Audio play failed:", e));
      setIsPlaying(true); // ADD THIS LINE - Set isPlaying to true when autoplay starts
    } else {
      setIsPlaying(false); // ADD THIS LINE - Ensure isPlaying is false when not autoplaying
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("ended", endedHandler);
        audioRef.current.removeEventListener("timeupdate", timeupdateHandler);
        audioRef.current.src = "";
      }
    };
  }, [props.audio, props.audioObject?.autoPlay, props.promptVisible, tabHasFocus]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Capture handler refs at effect execution time
    const endedHandler = endedHandlerRef.current;
    const timeupdateHandler = timeupdateHandlerRef.current;

    audio.addEventListener("ended", endedHandler);
    audio.addEventListener("timeupdate", timeupdateHandler);

    return () => {
      audio.removeEventListener("ended", endedHandler);
      audio.removeEventListener("timeupdate", timeupdateHandler);
    };
  }, [props.audio]);

  useEffect(() => {
    if (!props.editMode) {
      if (tabHasFocus && props.audioObject?.autoPlay && !props.promptVisible) {
        togglePlay();
      }
      if (isPlaying && !tabHasFocus) {
        audioRef.current.pause();
        setIsPlaying(!isPlaying);
        // stopAudio();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.audioTrigger, tabHasFocus, props?.promptVisible]);

  // reveal controls override
  useEffect(() => {
    if (props.playOverride !== null) {
      if (isPlaying || props.playOverride) {
        togglePlay();
      }
      if (isPlaying && !tabHasFocus) {
        stopAudio();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.playOverride]);

  // pass audio status
  useEffect(() => {
    props.audioPlaying(isPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = props?.audioObject?.loop || false;
    }
  }, [props?.audioObject?.loop]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = props?.audioObject?.customVolume
        ? props?.audioObject?.volumeValue ?? 1.0
        : 1.0;
    }
  }, [props?.audioObject]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else if (tabHasFocus) {
      audioRef.current.play().catch((e) => console.error("Play failed:", e));
    }
    setIsPlaying(!isPlaying);
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const handleMouseEnter = () => {
    if (props.disableDraggable) {
      props.disableDraggable(true);
    }
  };

  const handleMouseLeave = () => {
    if (props.disableDraggable) {
      props.disableDraggable(false);
    }
  };

  const player = () => {
    return (
      <Stack
        className="audio-player"
        direction="row"
        justifyContent="start"
        sx={{
          width: "200px",
        }}
      >
        <Row className="d-flex w-100 m-0 justify-content-center">
          <Col className={`player-section player p-0`}>
            <Row>
              <Col xs="auto" className="part btn">
                <IconButton
                  className="play-btn control-btn"
                  onClick={() => {
                    togglePlay();
                  }}
                >
                  {" "}
                  {isPlaying ? <PauseIcon /> : <PlayArrow />}
                </IconButton>
              </Col>
              <Col
                className="part track"
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={() => handleMouseLeave()}
              >
                <Slider
                  className="control-slider"
                  size="small"
                  min={0}
                  step={0.1}
                  value={audioTime}
                  max={audioRef.current?.duration || 0}
                  onChange={(event) => {
                    setAudioTime(event.target.value);
                    if (audioRef.current) {
                      audioRef.current.currentTime = event.target.value;
                    }
                  }}
                />
              </Col>
              <Col xs="auto" className="part btn">
                <IconButton
                  className="mute-btn control-btn"
                  onClick={() => {
                    setIsMuted(!isMuted);
                  }}
                >
                  {isMuted ? <VolumeOff /> : <VolumeUp />}
                </IconButton>
              </Col>
            </Row>
          </Col>
        </Row>
      </Stack>
    );
  };

  return (
    <>
      <Box className="player-container">{player()}</Box>
    </>
  );
}
